import { BigNumber } from 'bignumber.js';
import { useEffect, useMemo, useState } from 'react';

import { isNativeSuiAsset } from '@/shared/modules/wallet/is-native-sui-asset';
import {
  isPositiveAmountWithinBalance,
  parseAmountToBaseUnits,
} from '@/shared/modules/wallet/send/amount';
import { getSendDisabledReason } from '@/shared/modules/wallet/send/get-send-disabled-reason';
import { useSendableCoinCounts } from '@/shared/modules/wallet/send/use-sendable-coin-counts';
import { CryptoAsset } from '@/shared/modules/wallet/use-crypto-assets';

import { fmtAmount, fmtUsdShort } from '../../crypto-assets/formatters';
import { useSendRecipient } from './use-send-recipient';

const INSUFFICIENT_BALANCE = 'Amount exceeds your available balance.';
const NO_ASSETS = 'No assets available to send.';

const mostValuable = (assets: CryptoAsset[]): CryptoAsset | undefined =>
  [...assets].sort((a, b) => {
    const aValue = a.usdValue == null ? a.balance : new BigNumber(a.usdValue);
    const bValue = b.usdValue == null ? b.balance : new BigNumber(b.usdValue);
    return bValue.comparedTo(aValue) ?? 0;
  })[0];

const defaultCoin = (assets: CryptoAsset[]): CryptoAsset | undefined =>
  assets.find(isNativeSuiAsset) ?? mostValuable(assets);

const useSendCoin = (assets: CryptoAsset[], initialCoin: CryptoAsset | undefined) => {
  const initialCoinType = () => (initialCoin ?? defaultCoin(assets))?.coinType;
  const [coinType, setCoinType] = useState<string | undefined>(initialCoinType);

  useEffect(() => {
    if (!coinType && initialCoin) {
      setCoinType(initialCoin.coinType);
    }
  }, [coinType, initialCoin]);

  const coin = useMemo(
    () => assets.find((asset) => asset.coinType === coinType) ?? assets[0],
    [assets, coinType],
  );

  return { coin, setCoinType, resetCoin: () => setCoinType(initialCoinType()) };
};

const useSendDisabledReason = (
  coin: CryptoAsset | undefined,
  senderAddress: string | undefined,
  open: boolean,
): string | null => {
  const coinCounts = useSendableCoinCounts(
    coin ? [coin.coinType] : [],
    senderAddress,
    open && !!coin,
  );

  if (!coin) {
    return NO_ASSETS;
  }

  return getSendDisabledReason({ balance: coin.balance, coinInfo: coinCounts[coin.coinType] });
};

const describeSendAmount = (coin: CryptoAsset | undefined, amount: string) => {
  const numericAmount = Number(amount);
  const amountValue = new BigNumber(amount);
  const exceedsBalance =
    coin != null && amountValue.isFinite() && amountValue.isGreaterThan(coin.balance);
  const rawAmount = coin ? parseAmountToBaseUnits(amount, coin.decimals) : null;
  return {
    amountUsd: coin?.price != null && numericAmount > 0 ? coin.price * numericAmount : null,
    amountError: exceedsBalance ? INSUFFICIENT_BALANCE : '',
    rawAmount,
    amountValid:
      rawAmount !== null && coin != null && isPositiveAmountWithinBalance(amount, coin.balance),
  };
};

const formatBalanceLabel = (coin: CryptoAsset | undefined): string => {
  if (!coin) {
    return '';
  }

  const balance =
    coin.usdValue == null
      ? `${fmtAmount(coin.balance)} ${coin.symbol}`
      : fmtUsdShort(coin.usdValue);
  return `Balance: ${balance}`;
};

interface SendDraftOptions {
  open: boolean;
  assets: CryptoAsset[];
  senderAddress: string | undefined;
  initialCoin?: CryptoAsset;
}

export const useSendDraft = ({ open, assets, senderAddress, initialCoin }: SendDraftOptions) => {
  const { coin, setCoinType, resetCoin } = useSendCoin(assets, initialCoin);
  const [amount, setAmount] = useState('');
  const recipient = useSendRecipient();
  const disabledReason = useSendDisabledReason(coin, senderAddress, open);
  const { rawAmount, amountValid, ...amountView } = describeSendAmount(coin, amount);

  const reset = () => {
    setAmount('');
    recipient.clearRecipient();
    resetCoin();
  };

  const view = {
    ...recipient,
    ...amountView,
    coin,
    amount,
    balanceLabel: formatBalanceLabel(coin),
    disabledReason,
    canContinue: !disabledReason && !!recipient.recipientAddress && amountValid,
    setAmount,
    setCoinType,
  };
  return { view, rawAmount, reset };
};
